import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import { handleApiError } from "@utils/errorHandler";
import {
  TABLE_USER_LIST,
  PAGE_SIZE,
  FILTER_TYPE_BUTTON,
} from "@utils/constants";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { columnHeader } from "@utils/utils";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import GeneralInfo from "@modules/ClientUsers/ClientUsersList/components/GeneralInfo/GeneralInfo";
import Status from "@modules/ClientUsers/ClientUsersList/components/Status/Status";
import Action from "@modules/ClientUsers/ClientUsersList/components/Action/Action";

const CLIENT_USERS_ENDPOINT = "/general/users";

const PrepareDataTable = (props) => {
  const { t, columnList } = props;

  const [data, setData] = useState([]);
  const [activeStatus, setActiveStatus] = useState(1);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState([]);

  const updateRefeshStatus = () => {
    setRefreshState(true);
  };

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="id" />,
    },
    {
      Header: t("CONTACT_NAME"),
      title: "CONTACT_NAME",
      accessor: "name",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="name" />,
    },
    {
      Header: t("CONTACT_EMAIL"),
      title: "CONTACT_EMAIL",
      accessor: "email",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="email" />,
    },
    {
      Header: t("COMPANY"),
      title: "COMPANY",
      accessor: "company",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="company" />
      ),
    },
    {
      Header: t("STATUS"),
      title: "STATUS",
      accessor: "statusInfo",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <Status data={value} />,
    },
    {
      Header: t("CONTACT_LAST_LOGIN"),
      title: "CONTACT_LAST_LOGIN",
      accessor: "lastLogin",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="lastLogin" />
      ),
    },
    {
      Header: t("CREATED_AT"),
      title: "CREATED_AT",
      accessor: "createdAt",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="createdAt" />
      ),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <Action data={value} updateRefeshStatus={updateRefeshStatus} />
      ),
    },
  ];

  columns = columnHeader(columns, columnList);

  /**
   * Method to fetch set of records based on the filters applied.
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });

    setLoading(true);
    axios
      .get(CLIENT_USERS_ENDPOINT, {
        params: {
          limit: pageSize,
          page: currentPage,
          orderBy: sort ? sort.dir : "desc",
          orderColumn: sort ? sort.column : "id",
          search: globalFilter || "",
          status: activeStatus === 1 ? JSON.stringify([1,3]) : JSON.stringify([2]),
        },
      })
      .then((response) => {
        const usersList = response?.data?.users || [];
        if (usersList.length > 0) {
          const responseData = usersList.map((user) => {
            const status = user.status;
            return {
              id: { id: user.userId, status },
              name: {
                name: `${user.lastName || ""}${
                  user.lastName && user.firstName ? ", " : ""
                }${user.firstName || ""}`,
                status,
              },
              email: { email: user.email, status },
              company: { company: user.company?.name || "—", status },
              statusInfo: { status, statusLabel: user.statusLabel },
              lastLogin: {
                lastLogin: user.lastLogin
                  ? moment(user.lastLogin).format("DD/MM/YYYY")
                  : "—",
                status,
              },
              createdAt: {
                createdAt: user.createdAt
                  ? moment(user.createdAt).format("DD/MM/YYYY")
                  : "—",
                status,
              },
              action: { id: user.userId, status },
            };
          });
          setData([...responseData]);
          setPageCount(response?.data?.filteredRecords || 0);
        } else {
          setData([]);
          setPageCount(0);
        }
        setLoading(false);
      })
      .catch((error) => {
        setData([]);
        setPageCount(0);
        setLoading(false);
        handleApiError(error);
      });
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
  }, [isRefreshDataAgain]);

  useEffect(() => {
    // when the status filter changes, reset to first page and trigger refresh
    setCurrentPageToFirst(true);
    setRefreshState(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatus]);

  const changeStatus = () => {
    setActiveStatus(activeStatus === 1 ? 2 : 1);
  };

  return (
    <EncloseTable
      type={TABLE_USER_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={PAGE_SIZE}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      loading={loading}
      additionalFilters={additionalFilters}
      setAdditionalFilters={setAdditionalFilters}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      extraFilters={[
        {
          type: FILTER_TYPE_BUTTON,
          label: "CLIENT_USERS_FILTER_ACTIVE_ONLY",
          name: "activeUsers",
          labelClass: "client-users-list__active-label",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status: activeStatus,
          changeStatus: { changeStatus },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  columnList: PropTypes.array.isRequired,
};

export default withTranslation()(PrepareDataTable);
